import { Component, type ReactNode } from "react";

/**
 * Catches render-time crashes inside any subtree (e.g. a single page) so the
 * rest of the app stays usable. Logs to console for ops; shows a soft retry
 * card to the user. Routing-level boundary lives in main.tsx.
 */
interface State {
  err: Error | null;
}

export class PageErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: { componentStack?: string }) {
    // eslint-disable-next-line no-console
    console.error("[PageErrorBoundary]", err, info.componentStack);
  }

  override render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="grid h-full place-items-center px-6">
        <div className="max-w-sm space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <div className="text-3xl">😵‍💫</div>
          <p className="text-sm text-amber-900">Something went sideways on this page.</p>
          <p className="text-xs text-amber-700">{this.state.err.message}</p>
          <button
            onClick={() => this.setState({ err: null })}
            className="rounded-xl bg-amber-900 px-4 py-2 text-xs text-white"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
